import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useComposedRefs } from 'radix-ui/internal';
import { Cross2Icon } from '@radix-ui/react-icons';
import { InsideDialogProvider } from '@/components/ui/inside-dialog-context';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

export const dialogOverlayClasses =
  'fixed inset-0 z-50 bg-ui-light/80 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-ui-container/80';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(dialogOverlayClasses, className)} {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const [contentElement, setContentElement] = React.useState<HTMLDivElement | null>(null);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={useComposedRefs(ref, setContentElement)}
        className={cn(
          /**
           * grid-cols-[minmax(0,1fr)]: an auto track has a min-content floor, so long unbroken names spill past max-w-lg.
           * Centre with m-auto, not a transform, which would make this the containing block for fixed children.
           */
          'fixed inset-0 z-50 m-auto grid h-fit w-full max-w-lg grid-cols-[minmax(0,1fr)] gap-y-px border border-ui-void-gap bg-ui-void-gap shadow-lg duration-200 focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
        // NOTE: Closing the dialog doesn't remove pointer events from the body, so we are doing it manually.
        // Potential fix is to upgrade radixui dependencies
        // https://github.com/radix-ui/primitives/issues/1241
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.body.style.pointerEvents = '';
        }}
      >
        <InsideDialogProvider value={contentElement}>{children}</InsideDialogProvider>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  children,
  onClose,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { onClose?: () => void }) => {
  const tCommon = useTranslations('common');

  return (
    <div
      className="flex flex-row items-center justify-between border-t border-t-toolbar-bevel bg-ui-medium px-2 py-3"
      {...props}
    >
      <div className={cn(className, 'line-clamp-1 wrap-anywhere')}>{children}</div>
      <DialogPrimitive.Close
        onClick={() => onClose?.()}
        tabIndex={-1}
        className="opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none disabled:opacity-70"
      >
        <Cross2Icon className="icon-6" />
        <span className="sr-only">{tCommon('close')}</span>
      </DialogPrimitive.Close>
    </div>
  );
};
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-row bg-ui-medium sm:justify-end', className)} {...props}>
    <div className="flex w-full flex-col bg-ui-void-gap sm:h-12 sm:w-auto sm:flex-row sm:space-x-px sm:pl-px">
      {children}
    </div>
  </div>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold leading-none text-text-base', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    asChild
    className={cn('bg-ui-panel text-base text-text-gray', className)}
    {...props}
  >
    <div className={className}>{props.children}</div>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
